/**
 * DriverTrainingPage — `/driver-portal/training`.
 *
 * C5: Wired to GET /v1/driver-portal/me/training. The backend returns
 * training_completion rows. The static course catalog below is the
 * authoritative list per SRS; completion status overlays the catalog.
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, CheckCircle2, FileCheck, PlayCircle } from 'lucide-react';

import { getDriverPortalTraining } from '../../lib/api/driver-portal';
import { PageHeader } from '../shared/StaffUI';
import { useDriverPortalContext } from './DriverPortalLayout';

const CATALOG: readonly { code: string; title: string; mandatory: boolean }[] = [
  { code: 'DEF_RIDING_REFRESHER', title: 'Defensive Riding Refresher',  mandatory: true },
  { code: 'POD_BEST_PRACTICE',    title: 'POD best practice',           mandatory: true },
  { code: 'PANIC_SOS_WALKTHROUGH',title: 'Panic / SOS walkthrough',     mandatory: true },
  { code: 'DANGEROUS_GOODS',      title: 'Dangerous Goods Awareness',   mandatory: true },
  { code: 'NDPR_DATA_HANDLING',   title: 'NDPR / Data Handling',        mandatory: false },
  { code: 'CUSTOMER_SERVICE',     title: 'Customer service essentials', mandatory: false },
];

export function DriverTrainingPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();

  const training = useQuery({
    queryKey: ['driver-portal', 'training', driverId],
    queryFn: () => getDriverPortalTraining(driverId),
    retry: false,
    staleTime: 60_000,
  });

  const completionMap = new Map(
    (training.data?.completions ?? []).map((c) => [c.courseCode, c]),
  );

  const nextDue = CATALOG.find(
    (c) => c.mandatory && !completionMap.has(c.code),
  );

  return (
    <div>
      <PageHeader
        title="Training & SOPs"
        description="Mandatory + optional courses. Mandatory courses gate clock-in."
      />

      {training.isPending ? (
        <p className="text-sm text-muted my-4">Loading training status…</p>
      ) : null}

      {/* Next mandatory due banner */}
      {nextDue ? (
        <div className="bg-white border border-border rounded-xl p-5 mb-5 mt-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-nipost-green/10 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-nipost-green" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900" data-testid="next-mandatory-course">
              {nextDue.title}
            </p>
            <p className="text-xs text-muted">
              Next mandatory refresher. Complete to maintain clock-in access.
            </p>
          </div>
          <button
            type="button"
            disabled
            title="Course player coming soon"
            className="inline-flex items-center gap-2 bg-gray-200 text-gray-500 text-sm font-semibold px-4 py-2 rounded-lg cursor-not-allowed"
          >
            <PlayCircle className="w-4 h-4" /> Start now
          </button>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl p-5 mb-5 mt-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-success-light flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-success" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              All mandatory courses complete
            </p>
            <p className="text-xs text-muted">
              Your certifications are current. Check back when renewals are due.
            </p>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4" data-testid="training-catalog">
        {CATALOG.map((course) => {
          const completion = completionMap.get(course.code);
          return (
            <div key={course.code} className="bg-white border border-border rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    completion
                      ? 'bg-success-light'
                      : 'bg-gray-100'
                  }`}
                >
                  {completion ? (
                    <CheckCircle2 className="w-4 h-4 text-success" />
                  ) : (
                    <BookOpen className="w-4 h-4 text-gray-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-900 truncate">{course.title}</p>
                  <p className="text-[10px] text-muted">
                    {course.mandatory ? 'Mandatory' : 'Optional'}
                    {completion
                      ? ` · Completed ${new Date(completion.completedAt).toLocaleDateString()}`
                      : ' · Not completed'}
                  </p>
                  {completion ? (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 bg-gray-100 rounded h-1.5 overflow-hidden">
                        <div className="h-full bg-success" style={{ width: '100%' }} />
                      </div>
                      <p className="text-[11px] text-success font-semibold">Done</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 bg-gray-100 rounded h-1.5 overflow-hidden">
                        <div className="h-full bg-gray-300" style={{ width: '0%' }} />
                      </div>
                      <p className="text-[11px] text-muted">—/—</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
